import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPageCategories, createPageCategory, updatePageCategory, deletePageCategory } from '@/services/api';
import { PageCategory } from '../types/meta.types';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Tag, Plus, Pencil, Trash2, Search, Loader2 } from 'lucide-react';
import CategoryDialog from './CategoryDialog';
import { Input } from '@/components/ui/input';

export default function CategoriesList() {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<PageCategory | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: categories = [], isLoading } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
    });

    const createMutation = useMutation({
        mutationFn: createPageCategory,
        onSuccess: () => {
            toast({ title: 'Categoria criada!', description: 'A nova categoria foi adicionada com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['page-categories'] });
            setIsDialogOpen(false);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, name }: { id: number; name: string }) => updatePageCategory(id, { name }),
        onSuccess: () => {
            toast({ title: 'Categoria atualizada!', description: 'A categoria foi alterada com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['page-categories'] });
            setIsDialogOpen(false);
            setSelectedCategory(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deletePageCategory,
        onSuccess: () => {
            toast({ title: 'Categoria removida!', description: 'A categoria foi excluída com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['page-categories'] });
        },
    });

    const handleCreateNew = () => {
        setSelectedCategory(null);
        setIsDialogOpen(true);
    };

    const handleEdit = (category: PageCategory) => {
        setSelectedCategory(category);
        setIsDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Tem certeza que deseja remover esta categoria?')) {
            deleteMutation.mutate(id);
        }
    };

    const handleSubmit = (name: string) => {
        if (selectedCategory) {
            updateMutation.mutate({ id: selectedCategory.id, name });
        } else {
            createMutation.mutate({ name });
        }
    };

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Categorias</h1>
                    <p className="mt-2 text-gray-600">Gerencie as categorias internas das suas páginas.</p>
                </div>
                <Button
                    onClick={handleCreateNew}
                    className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Nova categoria
                </Button>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder="Buscar categoria..."
                        className="pl-9"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="bg-white rounded-lg p-16 flex flex-col items-center justify-center gap-4 border border-gray-100 shadow-sm">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    <p className="text-gray-500 font-medium tracking-tight">Carregando categorias...</p>
                </div>
            ) : filteredCategories.length > 0 ? (
                <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Páginas</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Atualização</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredCategories.map((category) => (
                                <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                                <Tag className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <span className="text-sm font-semibold text-gray-900">{category.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                {category.pages_count || 0} {category.pages_count === 1 ? 'página' : 'páginas'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {category.updated_at ? new Date(category.updated_at).toLocaleDateString('pt-BR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => handleEdit(category)} className="h-8 w-8 p-0 text-gray-600">
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)} className="h-8 w-8 p-0 text-red-600">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-white rounded-lg p-16 text-center shadow border border-gray-100 flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                        <Tag className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Sem categorias</h3>
                    <p className="text-gray-500 max-w-sm mx-auto mb-8">
                        {searchTerm ? 'Nenhuma categoria encontrada para o termo buscado.' : 'Comece cadastrando sua primeira categoria interna.'}
                    </p>
                    <Button
                        onClick={handleCreateNew}
                        className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-5 py-2 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                    >
                        Nova categoria
                    </Button>
                </div>
            )}

            <CategoryDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSubmit={handleSubmit}
                category={selectedCategory}
                isLoading={createMutation.isPending || updateMutation.isPending}
            />
        </div>
    );
}
